// src/lib/utils/colors/types.ts

/**
 * Comprehensive color utilities and conversion library types
 * Provides type-safe color conversions between multiple color spaces
 * with full TypeScript support and JSDoc documentation
 */

// Color space type definitions

/**
 * RGB color space - Red, Green, Blue components [0-255, 0-255, 0-255]
 * @typedef {readonly [r: number, g: number, b: number]} RGB
 */
export type RGB = readonly [r: number, g: number, b: number]

/**
 * RGBA color space - Red, Green, Blue, Alpha components [0-255, 0-255, 0-255, 0-1]
 * @typedef {readonly [r: number, g: number, b: number, a: number]} RGBA
 */
export type RGBA = readonly [r: number, g: number, b: number, a: number]

/**
 * HSL color space - Hue, Saturation, Lightness [0-360, 0-100, 0-100]
 * @typedef {readonly [h: number, s: number, l: number]} HSL
 */
export type HSL = readonly [h: number, s: number, l: number]

/**
 * HSLA color space - Hue, Saturation, Lightness, Alpha [0-360, 0-100, 0-100, 0-1]
 * @typedef {readonly [h: number, s: number, l: number, a: number]} HSLA
 */
export type HSLA = readonly [h: number, s: number, l: number, a: number]

/**
 * HSV color space - Hue, Saturation, Value [0-360, 0-100, 0-100]
 * @typedef {readonly [h: number, s: number, v: number]} HSV
 */
export type HSV = readonly [h: number, s: number, v: number]

/**
 * HSVA color space - Hue, Saturation, Value, Alpha [0-360, 0-100, 0-100, 0-1]
 * @typedef {readonly [h: number, s: number, v: number, a: number]} HSVA
 */
export type HSVA = readonly [h: number, s: number, v: number, a: number]

/**
 * HWB color space - Hue, Whiteness, Blackness [0-360, 0-100, 0-100]
 * @typedef {readonly [h: number, w: number, b: number]} HWB
 */
export type HWB = readonly [h: number, w: number, b: number]

/**
 * HWBA color space - Hue, Whiteness, Blackness, Alpha [0-360, 0-100, 0-100, 0-1]
 * @typedef {readonly [h: number, w: number, b: number, a: number]} HWBA
 */
export type HWBA = readonly [h: number, w: number, b: number, a: number]

/**
 * CMYK color space - Cyan, Magenta, Yellow, Key (Black) [0-100, 0-100, 0-100, 0-100]
 * @typedef {readonly [c: number, m: number, y: number, k: number]} CMYK
 */
export type CMYK = readonly [c: number, m: number, y: number, k: number]

/**
 * XYZ color space - CIE 1931 XYZ color space
 * @typedef {readonly [x: number, y: number, z: number]} XYZ
 */
export type XYZ = readonly [x: number, y: number, z: number]

/**
 * LAB color space - CIE L*a*b* color space
 * @typedef {readonly [l: number, a: number, b: number]} LAB
 */
export type LAB = readonly [l: number, a: number, b: number]

/**
 * OKLAB color space - Oklab perceptual color space
 * @typedef {readonly [l: number, a: number, b: number]} OKLAB
 */
export type OKLAB = readonly [l: number, a: number, b: number]

/**
 * LCH color space - CIE L*C*h° color space
 * @typedef {readonly [l: number, c: number, h: number]} LCH
 */
export type LCH = readonly [l: number, c: number, h: number]

/**
 * OKLCH color space - OKLCh color space
 * @typedef {readonly [l: number, c: number, h: number]} OKLCH
 */
export type OKLCH = readonly [l: number, c: number, h: number]

/**
 * HCG color space - Hue, Chroma, Grayness [0-360, 0-100, 0-100]
 * @typedef {readonly [h: number, c: number, g: number]} HCG
 */
export type HCG = readonly [h: number, c: number, g: number]

/**
 * Apple RGB color space - 16-bit RGB components [0-65535, 0-65535, 0-65535]
 * @typedef {readonly [r16: number, g16: number, b16: number]} Apple
 */
export type Apple = readonly [r16: number, g16: number, b16: number]

/**
 * Grayscale color space - Single gray component [0-100]
 * @typedef {readonly [gray: number]} Gray
 */
export type Gray = readonly [gray: number]

/**
 * Hexadecimal color string representation
 * @typedef {string} HEX
 */
export type HEX = string

/**
 * CSS color keyword string
 * @typedef {string} Keyword
 */
export type Keyword = string

/**
 * ANSI 16-color code [0-15]
 * @typedef {number} ANSI16
 */
export type ANSI16 = number

/**
 * ANSI 256-color code [0-255]
 * @typedef {number} ANSI256
 */
export type ANSI256 = number

/* eslint-disable @typescript-eslint/no-duplicate-type-constituents */
/**
 * Union type of all supported color formats
 * @typedef {RGB | RGBA | HSL | HSLA | HSV | HSVA | HWB | HWBA | CMYK | XYZ | LAB | OKLAB | LCH | OKLCH | HCG | Apple | Gray | HEX | Keyword | ANSI16 | ANSI256} ColorValue
 */
export type ColorValue =
  | RGB
  | RGBA
  | HSL
  | HSLA
  | HSV
  | HSVA
  | HWB
  | HWBA
  | CMYK
  | XYZ
  | LAB
  | OKLAB
  | LCH
  | OKLCH
  | HCG
  | Apple
  | Gray
  | HEX // string
  | Keyword // string
  | ANSI16 // number
  | ANSI256 // number
/* eslint-enable @typescript-eslint/no-duplicate-type-constituents */

/**
 * Color space identifiers
 * @typedef {'rgb' | 'rgba' | 'hsl' | 'hsla' | 'hsv' | 'hsva' | 'hwb' | 'hwba' | 'cmyk' | 'xyz' | 'lab' | 'oklab' | 'lch' | 'oklch' | 'hcg' | 'apple' | 'gray' | 'hex' | 'keyword' | 'ansi16' | 'ansi256'} ColorSpace
 */
export type ColorSpace =
  | 'rgb'
  | 'rgba'
  | 'hsl'
  | 'hsla'
  | 'hsv'
  | 'hsva'
  | 'hwb'
  | 'hwba'
  | 'cmyk'
  | 'xyz'
  | 'lab'
  | 'oklab'
  | 'lch'
  | 'oklch'
  | 'hcg'
  | 'apple'
  | 'gray'
  | 'hex'
  | 'keyword'
  | 'ansi16'
  | 'ansi256'

/**
 * Color harmony types for palette generation
 * @typedef {'monochromatic' | 'complementary' | 'analogous' | 'triadic' | 'tetradic' | 'split-complementary' | 'square' | 'double-complementary' | 'rectangle'} HarmonyType
 */
export type HarmonyType = 'monochromatic' | 'complementary' | 'analogous' | 'triadic' | 'tetradic' | 'split-complementary' | 'square' | 'double-complementary' | 'rectangle'

/**
 * WCAG accessibility levels
 * @typedef {'aa' | 'aaa'} WcagLevel
 */
export type WcagLevel = 'aa' | 'aaa'

/**
 * Color temperature classifications
 * @typedef {'warm' | 'cool' | 'neutral'} ColorTemperature
 */
export type ColorTemperature = 'warm' | 'cool' | 'neutral'

/**
 * Color brightness calculation methods
 * @typedef {'perceptual' | 'yiq'} BrightnessMethod
 */
export type BrightnessMethod = 'perceptual' | 'yiq'

/**
 * CSS color format types for string output
 * @typedef {'rgb' | 'rgba' | 'hsl' | 'hsla' | 'hex' | 'hwb'} CssColorFormat
 */
export type CssColorFormat = 'rgb' | 'rgba' | 'hsl' | 'hsla' | 'hex' | 'hwb'

/**
 * Color model metadata interface
 * @typedef {Object} ColorModel
 * @property {number} channels - Number of channels in the color model
 * @property {readonly string[]} labels - Channel labels for the color model
 * @property {readonly [number, number][]} [ranges] - Valid range for each channel
 */
export interface ColorModel {
  /** Number of channels in the color model */
  readonly channels: number
  /** Channel labels for the color model */
  readonly labels: readonly string[]
  /** Valid range for each channel */
  readonly ranges?: readonly [number, number][]
}

/**
 * Color conversion options interface
 * @typedef {Object} ColorConversionOptions
 * @property {number} [precision] - Precision for decimal places in output
 * @property {boolean} [clamp] - Whether to clamp values to valid ranges
 * @property {number} [alpha] - Alpha value to apply during conversion
 */
export interface ColorConversionOptions {
  /** Precision for decimal places in output */
  precision?: number
  /** Whether to clamp values to valid ranges */
  clamp?: boolean
  /** Alpha value to apply during conversion */
  alpha?: number
}

/**
 * Palette analysis result structure
 * @typedef {Object} PaletteAnalysis
 * @property {Object} variety - Variety metrics of the palette
 * @property {number} variety.hueSpread - Spread of hues in the palette (degrees)
 * @property {number} variety.saturationRange - Range of saturation values in the palette (0-100)
 * @property {number} variety.lightnessRange - Range of lightness values in the palette (0-100)
 * @property {Object} balance - Balance metrics of the palette
 * @property {number} balance.warmCoolRatio - Ratio of warm to cool colors (0-1, where 0.5 is balanced)
 * @property {number} balance.lightDarkRatio - Ratio of light to dark colors (0-1, where 0.5 is balanced)
 * @property {number} balance.vibrancyScore - Overall vibrancy score of the palette (0-1)
 * @property {Object} accessibility - Accessibility metrics for the palette
 * @property {number} accessibility.validPairs - Number of color pairs that meet accessibility standards
 * @property {Array.<[number, number]>} accessibility.problematicPairs - Array of index pairs that fail accessibility checks
 * @property {string[]} issues - List of detected issues or warnings for the palette
 * @property {number} score - Overall palette quality score (0-100)
 */
export type PaletteAnalysis = {
  /** Variety metrics of the palette */
  variety: {
    /** Spread of hues in the palette (degrees) */
    hueSpread: number
    /** Range of saturation values in the palette (0-100) */
    saturationRange: number
    /** Range of lightness values in the palette (0-100) */
    lightnessRange: number
  }
  /** Balance metrics of the palette */
  balance: {
    /** Ratio of warm to cool colors (0-1, where 0.5 is balanced) */
    warmCoolRatio: number
    /** Ratio of light to dark colors (0-1, where 0.5 is balanced) */
    lightDarkRatio: number
    /** Overall vibrancy score of the palette (0-1) */
    vibrancyScore: number
  }
  /** Accessibility metrics for the palette */
  accessibility: {
    /** Number of color pairs that meet accessibility standards */
    validPairs: number
    /** Array of index pairs that fail accessibility checks */
    problematicPairs: Array<[number, number]>
  }
  /** List of detected issues or warnings for the palette */
  issues: string[]
  /** Overall palette quality score (0-100) */
  score: number
}

/**
 * Color palette generation options
 * @typedef {Object} PaletteOptions
 * @property {number} count - Number of colors to generate
 * @property {HarmonyType} [harmony] - Type of harmony to use
 * @property {number} [minDistance] - Minimum perceptual distance between colors
 * @property {number} [lightnessRange] - Lightness range for variations
 * @property {boolean} [accessible] - Whether to ensure accessibility compliance
 * @property {WcagLevel} [wcagLevel] - WCAG level for accessibility
 */
export interface PaletteOptions {
  /** Number of colors to generate */
  count: number
  /** Type of harmony to use */
  harmony?: HarmonyType
  /** Minimum perceptual distance between colors */
  minDistance?: number
  /** Lightness range for variations */
  lightnessRange?: number
  /** Whether to ensure accessibility compliance */
  accessible?: boolean
  /** WCAG level for accessibility */
  wcagLevel?: WcagLevel
}

/**
 * Color analysis result interface
 * @typedef {Object} ColorAnalysis
 * @property {boolean} isDark - Whether the color is considered dark
 * @property {boolean} isLight - Whether the color is considered light
 * @property {ColorTemperature} temperature - Color temperature classification
 * @property {number} luminance - Relative luminance value
 * @property {'red' | 'green' | 'blue'} dominantChannel - Dominant color channel
 * @property {'low' | 'medium' | 'high'} saturationLevel - Color saturation level
 * @property {'low' | 'medium' | 'high'} lightnessLevel - Color lightness level
 */
export interface ColorAnalysis {
  /** Whether the color is considered dark */
  isDark: boolean
  /** Whether the color is considered light */
  isLight: boolean
  /** Color temperature classification */
  temperature: ColorTemperature
  /** Relative luminance value */
  luminance: number
  /** Dominant color channel */
  dominantChannel: 'red' | 'green' | 'blue'
  /** Color saturation level */
  saturationLevel: 'low' | 'medium' | 'high'
  /** Color lightness level */
  lightnessLevel: 'low' | 'medium' | 'high'
}

/**
 * Contrast analysis result interface
 * @typedef {Object} ContrastAnalysis
 * @property {number} ratio - Contrast ratio between colors
 * @property {'AAA' | 'AA' | 'FAIL'} wcagLevel - WCAG compliance level
 * @property {boolean} passesAA - Whether combination passes AA standard
 * @property {boolean} passesAAA - Whether combination passes AAA standard
 * @property {string} [recommendedText] - Recommended text color for accessibility
 */
export interface ContrastAnalysis {
  /** Contrast ratio between colors */
  ratio: number
  /** WCAG compliance level */
  wcagLevel: 'AAA' | 'AA' | 'FAIL'
  /** Whether combination passes AA standard */
  passesAA: boolean
  /** Whether combination passes AAA standard */
  passesAAA: boolean
  /** Recommended text color for accessibility */
  recommendedText?: string
}

/**
 * Color distance calculation methods
 * @typedef {'euclidean' | 'deltaE76' | 'deltaE94' | 'deltaE2000'} DistanceMethod
 */
export type DistanceMethod = 'euclidean' | 'deltaE76' | 'deltaE94' | 'deltaE2000'

/**
 * Color mixing blend modes
 * @typedef {'normal' | 'multiply' | 'screen' | 'overlay' | 'soft-light' | 'hard-light' | 'color-dodge' | 'color-burn' | 'darken' | 'lighten' | 'difference' | 'exclusion'} BlendMode
 */
export type BlendMode = 'normal' | 'multiply' | 'screen' | 'overlay' | 'soft-light' | 'hard-light' | 'color-dodge' | 'color-burn' | 'darken' | 'lighten' | 'difference' | 'exclusion'

/**
 * Color adjustment options
 * @typedef {Object} ColorAdjustmentOptions
 * @property {number} [hue] - Hue adjustment in degrees
 * @property {number} [saturation] - Saturation adjustment ratio
 * @property {number} [lightness] - Lightness adjustment ratio
 * @property {number} [alpha] - Alpha adjustment value
 * @property {boolean} [preserveLuminance] - Whether to preserve luminance during adjustments
 */
export interface ColorAdjustmentOptions {
  /** Hue adjustment in degrees */
  hue?: number
  /** Saturation adjustment ratio */
  saturation?: number
  /** Lightness adjustment ratio */
  lightness?: number
  /** Alpha adjustment value */
  alpha?: number
  /** Whether to preserve luminance during adjustments */
  preserveLuminance?: boolean
}

/**
 * Color theme generation options
 * @typedef {Object} ThemeOptions
 * @property {string} primary - Primary color for the theme
 * @property {string} [secondary] - Secondary color for the theme
 * @property {string} [accent] - Accent color for the theme
 * @property {'light' | 'dark' | 'auto'} [background] - Background color preference
 * @property {number} [shades] - Number of shades to generate per color
 * @property {boolean} [accessible] - Whether to ensure accessibility
 */
export interface ThemeOptions {
  /** Primary color for the theme */
  primary: string
  /** Secondary color for the theme */
  secondary?: string
  /** Accent color for the theme */
  accent?: string
  /** Background color preference */
  background?: 'light' | 'dark' | 'auto'
  /** Number of shades to generate per color */
  shades?: number
  /** Whether to ensure accessibility */
  accessible?: boolean
}

/**
 * Generated color theme interface
 * @typedef {Object} ColorTheme
 * @property {Object} primary - Primary color variations
 * @property {Object} secondary - Secondary color variations
 * @property {Object} accent - Accent color variations
 * @property {Object} background - Background colors
 * @property {Object} text - Text colors
 * @property {Object} surface - Surface colors
 * @property {Object} error - Error colors
 * @property {Object} warning - Warning colors
 * @property {Object} success - Success colors
 * @property {Object} info - Info colors
 */
export interface ColorTheme {
  /** Primary color variations */
  primary: {
    main: string
    light: string
    dark: string
    shades: string[]
  }
  /** Secondary color variations */
  secondary: {
    main: string
    light: string
    dark: string
    shades: string[]
  }
  /** Accent color variations */
  accent: {
    main: string
    light: string
    dark: string
    shades: string[]
  }
  /** Background colors */
  background: {
    primary: string
    secondary: string
    paper: string
  }
  /** Text colors */
  text: {
    primary: string
    secondary: string
    disabled: string
  }
  /** Surface colors */
  surface: {
    main: string
    variant: string
  }
  /** Error colors */
  error: {
    main: string
    light: string
    dark: string
  }
  /** Warning colors */
  warning: {
    main: string
    light: string
    dark: string
  }
  /** Success colors */
  success: {
    main: string
    light: string
    dark: string
  }
  /** Info colors */
  info: {
    main: string
    light: string
    dark: string
  }
}

/**
 * Semantic palette interface for design systems
 * @typedef {Object} SemanticPalette
 * @property {string[]} primary - Main brand color with shades
 * @property {string[]} secondary - Supporting brand color
 * @property {string[]} accent - Attention-grabbing colors
 * @property {string[]} neutral - Grays/backgrounds
 * @property {Object} semantic - Semantic color groups
 * @property {string[]} semantic.success - Success state colors
 * @property {string[]} semantic.warning - Warning state colors
 * @property {string[]} semantic.error - Error state colors
 * @property {string[]} semantic.info - Info state colors
 */
export interface SemanticPalette {
  /** Main brand color with shades */
  primary: string[]
  /** Supporting brand color */
  secondary: string[]
  /** Attention-grabbing colors */
  accent: string[]
  /** Grays/backgrounds */
  neutral: string[]
  /** Semantic color groups */
  semantic: {
    /** Success state colors */
    success: string[]
    /** Warning state colors */
    warning: string[]
    /** Error state colors */
    error: string[]
    /** Info state colors */
    info: string[]
  }
}

/**
 * CSS color keywords to RGB values mapping
 * @type {Record<string, RGB>}
 */
export const CSS_KEYWORDS: Record<string, RGB> = {
  aliceblue: [240, 248, 255],
  antiquewhite: [250, 235, 215],
  aqua: [0, 255, 255],
  aquamarine: [127, 255, 212],
  azure: [240, 255, 255],
  beige: [245, 245, 220],
  bisque: [255, 228, 196],
  black: [0, 0, 0],
  blanchedalmond: [255, 235, 205],
  blue: [0, 0, 255],
  blueviolet: [138, 43, 226],
  brown: [165, 42, 42],
  burlywood: [222, 184, 135],
  cadetblue: [95, 158, 160],
  chartreuse: [127, 255, 0],
  chocolate: [210, 105, 30],
  coral: [255, 127, 80],
  cornflowerblue: [100, 149, 237],
  cornsilk: [255, 248, 220],
  crimson: [220, 20, 60],
  cyan: [0, 255, 255],
  darkblue: [0, 0, 139],
  darkcyan: [0, 139, 139],
  darkgoldenrod: [184, 134, 11],
  darkgray: [169, 169, 169],
  darkgreen: [0, 100, 0],
  darkgrey: [169, 169, 169],
  darkkhaki: [189, 183, 107],
  darkmagenta: [139, 0, 139],
  darkolivegreen: [85, 107, 47],
  darkorange: [255, 140, 0],
  darkorchid: [153, 50, 204],
  darkred: [139, 0, 0],
  darksalmon: [233, 150, 122],
  darkseagreen: [143, 188, 143],
  darkslateblue: [72, 61, 139],
  darkslategray: [47, 79, 79],
  darkslategrey: [47, 79, 79],
  darkturquoise: [0, 206, 209],
  darkviolet: [148, 0, 211],
  deeppink: [255, 20, 147],
  deepskyblue: [0, 191, 255],
  dimgray: [105, 105, 105],
  dimgrey: [105, 105, 105],
  dodgerblue: [30, 144, 255],
  firebrick: [178, 34, 34],
  floralwhite: [255, 250, 240],
  forestgreen: [34, 139, 34],
  fuchsia: [255, 0, 255],
  gainsboro: [220, 220, 220],
  ghostwhite: [248, 248, 255],
  gold: [255, 215, 0],
  goldenrod: [218, 165, 32],
  gray: [128, 128, 128],
  green: [0, 128, 0],
  greenyellow: [173, 255, 47],
  grey: [128, 128, 128],
  honeydew: [240, 255, 240],
  hotpink: [255, 105, 180],
  indianred: [205, 92, 92],
  indigo: [75, 0, 130],
  ivory: [255, 255, 240],
  khaki: [240, 230, 140],
  lavender: [230, 230, 250],
  lavenderblush: [255, 240, 245],
  lawngreen: [124, 252, 0],
  lemonchiffon: [255, 250, 205],
  lightblue: [173, 216, 230],
  lightcoral: [240, 128, 128],
  lightcyan: [224, 255, 255],
  lightgoldenrodyellow: [250, 250, 210],
  lightgray: [211, 211, 211],
  lightgreen: [144, 238, 144],
  lightgrey: [211, 211, 211],
  lightpink: [255, 182, 193],
  lightsalmon: [255, 160, 122],
  lightseagreen: [32, 178, 170],
  lightskyblue: [135, 206, 250],
  lightslategray: [119, 136, 153],
  lightslategrey: [119, 136, 153],
  lightsteelblue: [176, 196, 222],
  lightyellow: [255, 255, 224],
  lime: [0, 255, 0],
  limegreen: [50, 205, 50],
  linen: [250, 240, 230],
  magenta: [255, 0, 255],
  maroon: [128, 0, 0],
  mediumaquamarine: [102, 205, 170],
  mediumblue: [0, 0, 205],
  mediumorchid: [186, 85, 211],
  mediumpurple: [147, 112, 219],
  mediumseagreen: [60, 179, 113],
  mediumslateblue: [123, 104, 238],
  mediumspringgreen: [0, 250, 154],
  mediumturquoise: [72, 209, 204],
  mediumvioletred: [199, 21, 133],
  midnightblue: [25, 25, 112],
  mintcream: [245, 255, 250],
  mistyrose: [255, 228, 225],
  moccasin: [255, 228, 181],
  navajowhite: [255, 222, 173],
  navy: [0, 0, 128],
  oldlace: [253, 245, 230],
  olive: [128, 128, 0],
  olivedrab: [107, 142, 35],
  orange: [255, 165, 0],
  orangered: [255, 69, 0],
  orchid: [218, 112, 214],
  palegoldenrod: [238, 232, 170],
  palegreen: [152, 251, 152],
  paleturquoise: [175, 238, 238],
  palevioletred: [219, 112, 147],
  papayawhip: [255, 239, 213],
  peachpuff: [255, 218, 185],
  peru: [205, 133, 63],
  pink: [255, 192, 203],
  plum: [221, 160, 221],
  powderblue: [176, 224, 230],
  purple: [128, 0, 128],
  red: [255, 0, 0],
  rosybrown: [188, 143, 143],
  royalblue: [65, 105, 225],
  saddlebrown: [139, 69, 19],
  salmon: [250, 128, 114],
  sandybrown: [244, 164, 96],
  seagreen: [46, 139, 87],
  seashell: [255, 245, 238],
  sienna: [160, 82, 45],
  silver: [192, 192, 192],
  skyblue: [135, 206, 235],
  slateblue: [106, 90, 205],
  slategray: [112, 128, 144],
  slategrey: [112, 128, 144],
  snow: [255, 250, 250],
  springgreen: [0, 255, 127],
  steelblue: [70, 130, 180],
  tan: [210, 180, 140],
  teal: [0, 128, 128],
  thistle: [216, 191, 216],
  tomato: [255, 99, 71],
  turquoise: [64, 224, 208],
  violet: [238, 130, 238],
  wheat: [245, 222, 179],
  white: [255, 255, 255],
  whitesmoke: [245, 245, 245],
  yellow: [255, 255, 0],
  yellowgreen: [154, 205, 50]
} as const

/**
 * Reverse mapping of RGB values to keyword names
 * @type {Record<string, string>}
 */
export const REVERSE_KEYWORDS: Record<string, string> = Object.fromEntries(
  Object.entries(CSS_KEYWORDS).map(([keyword, rgb]) => [rgb.join(','), keyword])
)

/**
 * Color model definitions with metadata
 * @type {Record<ColorSpace, ColorModel>}
 */
export const COLOR_MODELS: Record<ColorSpace, ColorModel> = {
  rgb: { channels: 3, labels: ['r', 'g', 'b'], ranges: [[0, 255], [0, 255], [0, 255]] },
  rgba: { channels: 4, labels: ['r', 'g', 'b', 'a'], ranges: [[0, 255], [0, 255], [0, 255], [0, 1]] },
  hsl: { channels: 3, labels: ['h', 's', 'l'], ranges: [[0, 360], [0, 100], [0, 100]] },
  hsla: { channels: 4, labels: ['h', 's', 'l', 'a'], ranges: [[0, 360], [0, 100], [0, 100], [0, 1]] },
  hsv: { channels: 3, labels: ['h', 's', 'v'], ranges: [[0, 360], [0, 100], [0, 100]] },
  hsva: { channels: 4, labels: ['h', 's', 'v', 'a'], ranges: [[0, 360], [0, 100], [0, 100], [0, 1]] },
  hwb: { channels: 3, labels: ['h', 'w', 'b'], ranges: [[0, 360], [0, 100], [0, 100]] },
  hwba: { channels: 4, labels: ['h', 'w', 'b', 'a'], ranges: [[0, 360], [0, 100], [0, 100], [0, 1]] },
  cmyk: { channels: 4, labels: ['c', 'm', 'y', 'k'], ranges: [[0, 100], [0, 100], [0, 100], [0, 100]] },
  xyz: { channels: 3, labels: ['x', 'y', 'z'] },
  lab: { channels: 3, labels: ['l', 'a', 'b'] },
  oklab: { channels: 3, labels: ['okl', 'oka', 'okb'] },
  lch: { channels: 3, labels: ['l', 'c', 'h'] },
  oklch: { channels: 3, labels: ['okl', 'okc', 'okh'] },
  hcg: { channels: 3, labels: ['h', 'c', 'g'], ranges: [[0, 360], [0, 100], [0, 100]] },
  apple: { channels: 3, labels: ['r16', 'g16', 'b16'], ranges: [[0, 65535], [0, 65535], [0, 65535]] },
  gray: { channels: 1, labels: ['gray'], ranges: [[0, 100]] },
  hex: { channels: 1, labels: ['hex'] },
  keyword: { channels: 1, labels: ['keyword'] },
  ansi16: { channels: 1, labels: ['ansi16'], ranges: [[0, 15]] },
  ansi256: { channels: 1, labels: ['ansi256'], ranges: [[0, 255]] }
} as const

// Constants for color calculations
/** LAB f(t) constant */
export const LAB_FT = (6 / 29) ** 3

/** D65 illuminant X component */
export const D65_X = 95.047

/** D65 illuminant Y component */
export const D65_Y = 100

/** D65 illuminant Z component */
export const D65_Z = 108.883

/** Default precision for color calculations */
export const DEFAULT_PRECISION = 2

/** WCAG contrast ratio thresholds */
export const WCAG_THRESHOLDS = {
  AA: 4.5,
  AAA: 7.0
} as const

/** Common color temperature ranges in Kelvin */
export const COLOR_TEMPERATURES = {
  CANDLE: 1900,
  TUNGSTEN: 2700,
  WARM_WHITE: 3000,
  COOL_WHITE: 4000,
  DAYLIGHT: 5500,
  OVERCAST: 6500,
  BLUE_SKY: 10000
} as const

/**
 * Utility type for extracting color channel values
 * @typedef {T extends readonly [...infer U] ? U : never} ColorChannels
 */
export type ColorChannels<T extends ColorValue> = T extends readonly [...infer U] ? U : never

/**
 * Utility type for color space validation
 * @typedef { ... } ValidColorSpace
 */
export type ValidColorSpace<T extends ColorValue> = 
  T extends RGB ? 'rgb' :
  T extends RGBA ? 'rgba' :
  T extends HSL ? 'hsl' :
  T extends HSLA ? 'hsla' :
  T extends HSV ? 'hsv' :
  T extends HSVA ? 'hsva' :
  T extends HWB ? 'hwb' :
  T extends HWBA ? 'hwba' :
  T extends CMYK ? 'cmyk' :
  T extends XYZ ? 'xyz' :
  T extends LAB ? 'lab' :
  T extends OKLAB ? 'oklab' :
  T extends LCH ? 'lch' :
  T extends OKLCH ? 'oklch' :
  T extends HCG ? 'hcg' :
  T extends Apple ? 'apple' :
  T extends Gray ? 'gray' :
  T extends HEX ? 'hex' :
  T extends Keyword ? 'keyword' :
  T extends ANSI16 ? 'ansi16' :
  T extends ANSI256 ? 'ansi256' :
  never

/**
 * Type guard for RGB color values
 * @param {ColorValue} value
 * @returns {value is RGB}
 */
export const isRGB = (value: ColorValue): value is RGB => 
  Array.isArray(value) && value.length === 3 && value.every(v => typeof v === 'number')

/**
 * Type guard for RGBA color values
 * @param {ColorValue} value
 * @returns {value is RGBA}
 */
export const isRGBA = (value: ColorValue): value is RGBA => 
  Array.isArray(value) && value.length === 4 && value.every(v => typeof v === 'number')

/**
 * Type guard for HSL color values
 * @param {ColorValue} value
 * @returns {value is HSL}
 */
export const isHSL = (value: ColorValue): value is HSL => 
  Array.isArray(value) && value.length === 3 && value.every(v => typeof v === 'number')

/**
 * Type guard for hex color strings
 * @param {ColorValue} value
 * @returns {value is HEX}
 */
export const isHEX = (value: ColorValue): value is HEX => 
  typeof value === 'string' && /^#([a-f\d]{3}|[a-f\d]{6})$/i.test(value)

/**
 * Type guard for CSS keyword strings
 * @param {ColorValue} value
 * @returns {value is Keyword}
 */
export const isKeyword = (value: ColorValue): value is Keyword => 
  typeof value === 'string' && value.toLowerCase() in CSS_KEYWORDS
